/**
 * Where one client stands on everything asked of it, and what it decided.
 *
 * Read-only on purpose. Staff can see a client's consents but never give,
 * decline or withdraw one on its behalf: a consent is the client's own act,
 * and one recorded by somebody else would be worth nothing as evidence.
 */

import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  ApiError,
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  LoadingState,
  StatusChip,
  api,
  formatDate,
  formatDateTime,
} from '@connect/web-shared';

import { RecordEvidence } from './consent-records-page';
import { STATUS_TONES, legalKeys, type ClientConsents } from './types';

export function ClientConsentsPanel({ clientPublicId }: { clientPublicId: string }) {
  const [open, setOpen] = useState<string | null>(null);
  const consents = useQuery({
    queryKey: legalKeys.client(clientPublicId),
    queryFn: () => api.get<ClientConsents>(`/legal/clients/${clientPublicId}/consents`),
  });

  if (consents.isLoading) return <LoadingState label="Loading consents" />;
  if (consents.isError || !consents.data) {
    return (
      <ErrorState
        message={consents.error instanceof ApiError ? consents.error.message : undefined}
        onRetry={() => void consents.refetch()}
      />
    );
  }

  const { requirements, pending_mandatory: blocking, history, history_total: total } =
    consents.data;

  return (
    <div className="space-y-4">
      {blocking > 0 ? (
        <Alert tone="warning" title="Waiting on the client">
          {blocking === 1
            ? 'One mandatory requirement is not accepted, so the client is held on the consent screen.'
            : `${blocking} mandatory requirements are not accepted, so the client is held on the consent screen.`}
        </Alert>
      ) : null}

      <Card>
        <CardHeader title="Now" description="Everything currently asked of this client." />
        {requirements.length === 0 ? (
          <CardBody>
            <p className="text-sm text-muted">Nothing is asked of this client.</p>
          </CardBody>
        ) : (
          <ul className="divide-y divide-line">
            {requirements.map((state) => (
              <li
                key={state.requirement_public_id}
                className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5"
              >
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-1.5 text-sm text-heading">
                    {state.title}
                    {state.mandatory ? <Badge tone="danger">Mandatory</Badge> : null}
                  </p>
                  <p className="text-2xs text-faint">
                    {state.document_title} · {state.current_version.label}
                    {state.decided_at
                      ? ` · decided ${formatDate(state.decided_at)}${
                          state.decided_version &&
                          state.decided_version.public_id !== state.current_version.public_id
                            ? ` on ${state.decided_version.label}`
                            : ''
                        }`
                      : ''}
                  </p>
                </div>
                <StatusChip value={state.status} tones={STATUS_TONES} />
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <CardHeader
          title="History"
          description={
            total > history.length
              ? `The latest ${history.length} of ${total} decisions. The full log is under Consent records.`
              : 'Every decision, newest first.'
          }
        />
        {history.length === 0 ? (
          <CardBody>
            <p className="text-sm text-muted">No decisions yet.</p>
          </CardBody>
        ) : (
          <table className="w-full text-sm">
            <tbody className="divide-y divide-line">
              {history.map((row) => {
                const expanded = open === row.public_id;
                return (
                  <Fragment key={row.public_id}>
                    <tr
                      className="cursor-pointer hover:bg-surface-2"
                      onClick={() => setOpen(expanded ? null : row.public_id)}
                    >
                      <td className="w-8 pl-3 text-faint">
                        {expanded ? (
                          <ChevronDown className="size-4" />
                        ) : (
                          <ChevronRight className="size-4" />
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">{formatDateTime(row.created_at)}</td>
                      <td className="px-3 py-2">{row.requirement_title}</td>
                      <td className="px-3 py-2">
                        {row.version_label}
                        {row.superseded ? (
                          <Badge tone="neutral" className="ml-1.5">
                            Superseded
                          </Badge>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">
                        <StatusChip value={row.decision} tones={STATUS_TONES} />
                      </td>
                    </tr>
                    {expanded ? (
                      <tr>
                        <td />
                        <td colSpan={4} className="px-3 py-3">
                          <RecordEvidence row={row} />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
